/**
 * Review card for a bulk import, shown before anything is written.
 *
 * A generic before/after diff card doesn't fit an import: there is no "before"
 * text to diff against, and the content that matters is a count breakdown plus
 * a list of new taxonomy/geography values — some of which are likely typos and
 * need a per-item decision, not a single approve/reject on the whole batch.
 */

import { useState, useMemo } from "react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Checkbox } from "@/components/ui/checkbox"
import { cn } from "@/lib/utils"
import {
  FileSpreadsheet, Plus, Pencil, AlertTriangle, ChevronDown, ChevronRight,
  Check, X,
} from "lucide-react"
import type { ImportPlan, NewValue } from "./import"

export interface PendingImportData {
  sheetName: string
  rowCount: number
  plan: ImportPlan
  /** Rows this run is limited to, if the caller passed a trial limit. */
  limit?: number
}

interface ImportReviewProps {
  data: PendingImportData
  /** Approved keys are exactly the NewValue.key strings the user checked. */
  onApprove: (approvedKeys: string[]) => void
  onReject: () => void
}

const KIND_LABEL: Record<string, string> = {
  provider: "Provider", country: "Country", region: "Region", city: "City",
  serviceCategory: "Category", tag: "Tag", population: "Population", accessibility: "Accessibility",
}

export function ImportReview({ data, onApprove, onReject }: ImportReviewProps) {
  const { plan } = data

  // Likely typos start unchecked — the whole point of flagging similarTo is
  // that these should need a deliberate opt-in, not be swept in with everything
  // else. Everything else defaults to approved, since that's the common case.
  const [approved, setApproved] = useState<Set<string>>(
    () => new Set(plan.newValues.filter(v => !v.similarTo).map(v => v.key)),
  );
  const [issuesOpen, setIssuesOpen] = useState(false)

  const toggle = (key: string) =>
    setApproved(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  const grouped = useMemo(() => {
    const byKind = new Map<string, NewValue[]>()
    for (const v of plan.newValues) {
      const list = byKind.get(v.kind) ?? []
      list.push(v)
      byKind.set(v.kind, list)
    }
    return [...byKind.entries()]
  }, [plan.newValues])

  const flaggedCount = plan.newValues.filter(v => v.similarTo).length

  return (
    <div className="mb-3 overflow-hidden rounded-xl border shadow-xs">
      <div className="flex items-center gap-2 border-b bg-purple-50 px-4 py-2.5 dark:bg-purple-950/30">
        <FileSpreadsheet className="h-4 w-4 shrink-0 text-purple-700 dark:text-purple-400" />
        <span className="text-sm font-semibold text-purple-900 dark:text-purple-200">
          Import {data.limit ? `${data.limit} of ${data.rowCount}` : data.rowCount} row{data.rowCount === 1 ? "" : "s"} from {data.sheetName}
        </span>
      </div>

      <div className="grid grid-cols-3 gap-px bg-border">
        <Stat label="Create" value={plan.toCreate} tone="positive" />
        <Stat label="Update" value={plan.toUpdate} tone="neutral" />
        <Stat label="Skip" value={plan.toSkip} tone={plan.toSkip ? "warning" : "neutral"} />
      </div>

      {grouped.length > 0 && (
        <div className="border-t px-4 py-3">
          <p className="mb-2 text-xs font-medium text-muted-foreground">
            New records this import would create ({approved.size} of {plan.newValues.length} approved)
            {flaggedCount > 0 && ` — ${flaggedCount} flagged as possible typos`}
          </p>
          <ScrollArea className="max-h-64 [&_[data-slot=scroll-area-viewport]>:first-child]:block!">
            <div className="space-y-3 pr-3">
              {grouped.map(([kind, values]) => (
                <div key={kind}>
                  <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {KIND_LABEL[kind] ?? kind} ({values.length})
                  </p>
                  <div className="space-y-1">
                    {values.map(v => (
                      <label
                        key={v.key}
                        className={cn(
                          "flex cursor-pointer items-start gap-2 rounded px-1.5 py-1 text-sm hover:bg-muted/60",
                          v.similarTo && "bg-amber-50 dark:bg-amber-950/20",
                        )}
                      >
                        <Checkbox
                          checked={approved.has(v.key)}
                          onCheckedChange={() => toggle(v.key)}
                          className="mt-0.5"
                        />
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-1.5">
                            {approved.has(v.key)
                              ? <Plus className="h-3 w-3 shrink-0 text-emerald-600" />
                              : <span className="h-3 w-3 shrink-0" />}
                            <span className="truncate font-medium">{v.name}</span>
                            <Badge variant="outline" className="shrink-0 px-1 py-0 text-[10px]">
                              {v.rowCount} row{v.rowCount === 1 ? "" : "s"}
                            </Badge>
                          </span>
                          {v.similarTo && (
                            <span className="flex items-center gap-1 text-[11px] text-amber-700 dark:text-amber-400">
                              <AlertTriangle className="h-3 w-3 shrink-0" />
                              Close to existing "{v.similarTo}" — probably meant to match it, not create a new one
                            </span>
                          )}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>
      )}

      {plan.issues.length > 0 && (
        <div className="border-t px-4 py-2.5">
          <button
            onClick={() => setIssuesOpen(o => !o)}
            className="flex w-full items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
          >
            {issuesOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            <Pencil className="h-3.5 w-3.5" />
            {plan.issues.length} row issue{plan.issues.length === 1 ? "" : "s"} — importable, but worth a look
          </button>
          {issuesOpen && (
            <ScrollArea className="mt-2 max-h-40 [&_[data-slot=scroll-area-viewport]>:first-child]:block!">
              <div className="space-y-1 pr-3">
                {plan.issues.map((iss, i) => (
                  <p key={i} className="text-[11px] text-muted-foreground">
                    <span className="font-mono">row {iss.row}</span>{" "}
                    <span className="text-foreground">{iss.field}</span>: {iss.message}
                  </p>
                ))}
              </div>
            </ScrollArea>
          )}
        </div>
      )}

      <div className="flex items-center justify-between gap-3 border-t bg-muted/30 px-4 py-3">
        <p className="text-xs text-muted-foreground">
          Unapproved new values are left empty on their rows rather than created.
        </p>
        <div className="flex shrink-0 gap-2">
          <Button variant="outline" size="sm" onClick={onReject} className="gap-1.5">
            <X className="h-3.5 w-3.5" /> Cancel
          </Button>
          <Button size="sm" onClick={() => onApprove([...approved])} className="gap-1.5 bg-emerald-600 hover:bg-emerald-700">
            <Check className="h-3.5 w-3.5" /> Import
          </Button>
        </div>
      </div>
    </div>
  )
}

function Stat({ label, value, tone }: { label: string; value: number; tone: "positive" | "neutral" | "warning" }) {
  return (
    <div className="bg-background px-4 py-3 text-center">
      <p
        className={cn(
          "text-xl font-semibold tabular-nums",
          tone === "positive" && "text-emerald-600",
          tone === "warning" && value > 0 && "text-amber-600",
        )}
      >
        {value}
      </p>
      <p className="text-[11px] text-muted-foreground">{label}</p>
    </div>
  )
}
